












































































































































































































































































import { AxiosError } from 'axios';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { PropType } from 'vue';
import { mapActions } from 'vuex';
import { get } from 'vuex-pathify';

import {
  IPaymentRequisition,
  IPaymentRequisitionComment,
  PaymentRequisitionComment,
  PaymentRequisitionCommentAttachment
} from '@/models/payment-requisition';
import { IAttachment, IUser } from '@/models';
import { ErrorManager, ISystemError } from '@/models/error';
import {
  createFile,
  diffDeep,
  isHtmlTextEmpty,
  setErrorFromAxiosError
} from '@/utils/helpers';
import auth from '@/mixins/auth';
import snackbar from '@/mixins/snackbar';
import mixins from '@/utils/mixins';

import CommentAttachmentList from '../common/CommentAttachmentList.vue';
import MentionProvider from '../mention/MentionProvider.vue';
import QuillEditor from '../common/QuillEditor.vue';
import DeleteConfirmationDialog from '../common/DeleteConfirmationDialog.vue';

export default mixins(auth, snackbar).extend({
  name: 'PaymentRequisitionCommentListItem',

  components: {
    CommentAttachmentList,
    DeleteConfirmationDialog,
    MentionProvider,
    QuillEditor
  },

  props: {
    paymentRequisition: {
      type: Object as PropType<IPaymentRequisition>,
      required: true
    },

    paymentRequisitionComment: {
      type: Object as PropType<IPaymentRequisitionComment>,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      isEditing: false,

      commentLoading: false,
      commentErrorMessage: '',
      commentDetailErrorMessage: '',

      // delete comment
      commentDeleteError: null as ISystemError | null,
      commentDeleteInProgress: false,
      selectedComment: cloneDeep(this.paymentRequisitionComment),
      deleteRequisitionCommentDialog: false,

      commentTextHtml: this.paymentRequisitionComment.getTextHtml(),
      mentionsLoading: false,

      deleteCommentAttachmentDialog: false,
      isAttachmentDeleteInProgress: false,
      attachmentDeleteError: null as ISystemError | null,
      selectedAttachment: null as null | IAttachment,
      attachments: [] as IAttachment[]
    };
  },

  computed: {
    isPaymentRequisitionCommentOwner(): boolean {
      return (
        this.getCurrentUserFullName === this.paymentRequisitionComment.owner
      );
    },

    canSaveComment(): boolean {
      return (
        !isHtmlTextEmpty(this.selectedComment.text) && !this.commentLoading
      );
    },

    mentionableUsers: get<IUser[]>(
      'paymentRequisition/getPaymentRequisitionMentionableUsers'
    )
  },

  methods: {
    ...mapActions({
      updatePaymentRequisitionComment:
        'paymentRequisition/updatePaymentRequisitionComment',
      deletePaymentRequisitionComment:
        'paymentRequisition/deletePaymentRequisitionComment',
      fetchPaymentRequisitionCommentsMentionableUsers:
        'paymentRequisition/fetchPaymentRequisitionCommentsMentionableUsers',
      addPaymentRequisitionCommentAttachments:
        'paymentRequisition/addPaymentRequisitionCommentAttachments',
      removePaymentRequisitionCommentAttachment:
        'paymentRequisition/removePaymentRequisitionCommentAttachment'
    }),

    timeFromNow: (date: Date | string) => {
      return moment(date).fromNow();
    },

    getInitials(name: string) {
      return name
        .toUpperCase()
        .split(' ')
        .map(n => n[0])
        .join('');
    },

    textHasDifference(): boolean {
      return this.selectedComment.text === this.paymentRequisitionComment.text;
    },

    async loadMentionableUsers() {
      this.clearAllErrors();

      this.mentionsLoading = true;
      try {
        await this.fetchPaymentRequisitionCommentsMentionableUsers(
          this.paymentRequisitionComment.id
        );
      } catch (error) {
        this.commentErrorMessage = setErrorFromAxiosError(
          error as AxiosError
        ).message;
      } finally {
        this.mentionsLoading = false;
      }
    },

    toggleEditingComment() {
      if (!this.isEditing) {
        // To reset the selected paymentRequisition comment data to the original when rediting
        this.selectedComment = cloneDeep(this.paymentRequisitionComment);
        this.commentTextHtml = this.paymentRequisitionComment.getTextHtml();
      }

      this.isEditing = !this.isEditing;
    },

    stopEditingComment() {
      if (!this.selectedComment.text || this.textHasDifference()) {
        this.isEditing = false;
      }
    },

    clearAllErrors(): void {
      this.commentErrorMessage = '';
      this.commentDeleteError = null;
      this.attachmentDeleteError = null;
    },

    cancelRequisitionCommentDeleteDialog() {
      this.deleteRequisitionCommentDialog = false;
    },

    addBaseAttachments(files: File[]) {
      this.attachments.push(
        ...files.map(
          file => new PaymentRequisitionCommentAttachment({ file: file })
        )
      );
    },

    cancelDeleteCommentAttachmentDialog() {
      this.deleteCommentAttachmentDialog = false;
    },

    removeCommentAttachment(attachment: IAttachment) {
      this.clearAllErrors();
      this.isAttachmentDeleteInProgress = true;

      this.removePaymentRequisitionCommentAttachment({
        requisitionId: this.paymentRequisition.id,
        commentId: this.paymentRequisitionComment.id,
        attachmentId: attachment.id
      })
        .then(() => {
          this.cancelDeleteCommentAttachmentDialog();
        })
        .catch(error => {
          this.attachmentDeleteError = setErrorFromAxiosError(error);
        })
        .finally(() => {
          this.isAttachmentDeleteInProgress = false;
        });
    },

    removeComment() {
      this.clearAllErrors();
      this.commentDeleteInProgress = true;
      this.deletePaymentRequisitionComment({
        requisitionId: this.paymentRequisition.id,
        commentId: this.paymentRequisitionComment.id
      })
        .then(() => {
          this.cancelRequisitionCommentDeleteDialog();
        })
        .catch(error => {
          this.commentDeleteError = setErrorFromAxiosError(error);
        })
        .finally(() => {
          this.commentDeleteInProgress = false;
        });
    },

    openAttachmentDeleteDialog(attachment: IAttachment) {
      this.clearAllErrors();
      this.selectedAttachment = attachment;
      this.deleteCommentAttachmentDialog = true;
    },

    editComment() {
      this.clearAllErrors();

      this.commentLoading = true;

      const parsedCommentMentionText = PaymentRequisitionComment.parseHtmlToMentionText(
        this.commentTextHtml
      );

      this.selectedComment.text = parsedCommentMentionText;

      const commentDelta = diffDeep(
        this.selectedComment,
        this.paymentRequisitionComment,
        true
      );

      return this.updatePaymentRequisitionComment({
        requisitionId: this.paymentRequisition.id,
        comment: commentDelta
      })
        .then(async () => {
          if (this.attachments.length !== 0) {
            await this.addPaymentRequisitionCommentAttachments({
              requisitionId: this.paymentRequisition.id,
              commentId: commentDelta.id,
              attachments: this.attachments
            });
            this.attachments = [];
          }

          this.selectedComment = cloneDeep(this.paymentRequisitionComment);
          this.isEditing = false;
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data.detail) {
              this.commentDetailErrorMessage = error.response.data.detail;
            }
          }
          this.commentErrorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.commentLoading = false;
        });
    },

    downloadAttachmentFile(attachment: IAttachment) {
      this.clearAllErrors();
      this.$services.fileLoader
        .downloadFile(attachment.file as string)
        .then(data => {
          createFile(data, attachment.filename);

          this.createSnack({
            color: 'success darken-1',
            type: 'success',
            right: true,
            useBtn: false,
            msg: `File attachment download successfully`,
            btnText: 'Ok',
            timeout: 6000
          });
        })
        .catch(error => {
          const err = setErrorFromAxiosError(error);
          this.createSnack({
            color: 'error',
            type: 'error',
            right: true,
            useBtn: false,
            msg: `Failed to download Attachment: ${err.detail || err.message}`,
            btnText: 'Close'
          });
        });
    }
  }
});
